'use client';

import React, { useEffect, useRef, memo } from 'react';

interface TradingViewWidgetProps {
  category?: string;
  title?: string;
  tags?: string[];
  articleText?: string;
}

// Mapping specific companies to their TradingView symbols
const companyToSymbol: Record<string, { s: string, d: string }> = {
  'apple': { s: "NASDAQ:AAPL", d: "Apple" },
  'microsoft': { s: "NASDAQ:MSFT", d: "Microsoft" },
  'nvidia': { s: "NASDAQ:NVDA", d: "Nvidia" },
  'amd': { s: "NASDAQ:AMD", d: "AMD" },
  'tsmc': { s: "NYSE:TSM", d: "TSMC" },
  'intel': { s: "NASDAQ:INTC", d: "Intel" },
  'asml': { s: "NASDAQ:ASML", d: "ASML" },
  'broadcom': { s: "NASDAQ:AVGO", d: "Broadcom" },
  'qualcomm': { s: "NASDAQ:QCOM", d: "Qualcomm" },
  'arm': { s: "NASDAQ:ARM", d: "ARM Holdings" },
  'tesla': { s: "NASDAQ:TSLA", d: "Tesla" },
  'amazon': { s: "NASDAQ:AMZN", d: "Amazon" },
  'google': { s: "NASDAQ:GOOGL", d: "Alphabet" },
  'alphabet': { s: "NASDAQ:GOOGL", d: "Alphabet" },
  'meta': { s: "NASDAQ:META", d: "Meta" },
  'pfizer': { s: "NYSE:PFE", d: "Pfizer" },
  'moderna': { s: "NASDAQ:MRNA", d: "Moderna" },
  'johnson & johnson': { s: "NYSE:JNJ", d: "Johnson & Johnson" },
  'boeing': { s: "NYSE:BA", d: "Boeing" },
  'bitcoin': { s: "CRYPTO:BTCUSD", d: "Bitcoin" },
  'ethereum': { s: "CRYPTO:ETHUSD", d: "Ethereum" },
  
  // Indian Companies (BSE / NSE)
  'reliance': { s: "BSE:RELIANCE", d: "Reliance Ind." },
  'tata motors': { s: "BSE:TATAMOTORS", d: "Tata Motors" },
  'tcs': { s: "BSE:TCS", d: "TCS" },
  'infosys': { s: "BSE:INFY", d: "Infosys" },
  'hdfc': { s: "BSE:HDFCBANK", d: "HDFC Bank" },
  'sbi': { s: "BSE:SBIN", d: "SBI" },
  'state bank of india': { s: "BSE:SBIN", d: "SBI" },
  'larsen & toubro': { s: "BSE:LT", d: "L&T" },
  'l&t': { s: "BSE:LT", d: "L&T" },
  'mahindra': { s: "BSE:M_M", d: "M&M" },
  'adani': { s: "BSE:ADANIENT", d: "Adani Ent." },
  'wipro': { s: "BSE:WIPRO", d: "Wipro" },
  'maruti': { s: "BSE:MARUTI", d: "Maruti Suzuki" },
  'itc': { s: "BSE:ITC", d: "ITC" },
  'bajaj finance': { s: "BSE:BAJFINANCE", d: "Bajaj Finance" },
  'sun pharma': { s: "BSE:SUNPHARMA", d: "Sun Pharma" },
  'jsw steel': { s: "BSE:JSWSTEEL", d: "JSW Steel" },
  'tata steel': { s: "BSE:TATASTEEL", d: "Tata Steel" },
  'ashok leyland': { s: "BSE:ASHOKLEY", d: "Ashok Leyland" },
  'bharat forge': { s: "BSE:BHARATFORG", d: "Bharat Forge" },
  'hal': { s: "BSE:HAL", d: "HAL" },
  'hindustan aeronautics': { s: "BSE:HAL", d: "HAL" },
  'bhel': { s: "BSE:BHEL", d: "BHEL" },
  'vedanta': { s: "BSE:VEDL", d: "Vedanta" },
  'hindalco': { s: "BSE:HINDALCO", d: "Hindalco" },
  'zomato': { s: "BSE:ZOMATO", d: "Zomato" },
  'paytm': { s: "BSE:PAYTM", d: "Paytm" },
  'jio': { s: "BSE:JIOFIN", d: "Jio Financial" },
  'bharat electronics': { s: "BSE:BEL", d: "BEL" },
  'bel': { s: "BSE:BEL", d: "BEL" },
  
  // Defense, Aerospace & Shipyards (India)
  'garden reach': { s: "BSE:GRSE", d: "GRSE" },
  'grse': { s: "BSE:GRSE", d: "GRSE" },
  'cochin shipyard': { s: "BSE:COCHINSHIP", d: "Cochin Shipyard" },
  'mazagon dock': { s: "BSE:MAZDOCK", d: "Mazagon Dock" },
  'mazagon': { s: "BSE:MAZDOCK", d: "Mazagon Dock" },
  'bharat dynamics': { s: "BSE:BDL", d: "Bharat Dynamics" },
  'bdl': { s: "BSE:BDL", d: "Bharat Dynamics" },
  'beml': { s: "BSE:BEML", d: "BEML" },
  
  // Automobile & Ancillary
  'bajaj auto': { s: "BSE:BAJAJ_AUTO", d: "Bajaj Auto" },
  'hero motocorp': { s: "BSE:HEROMOTOCO", d: "Hero MotoCorp" },
  'tvs motor': { s: "BSE:TVSMOTOR", d: "TVS Motor" },
  'eicher motors': { s: "BSE:EICHERMOT", d: "Eicher Motors" },
  'eicher': { s: "BSE:EICHERMOT", d: "Eicher Motors" },
  'bosch': { s: "BSE:BOSCHLTD", d: "Bosch" },
  'mrf': { s: "BSE:MRF", d: "MRF" },
  'apollo tyres': { s: "BSE:APOLLOTYRE", d: "Apollo Tyres" },
  
  // Pharma & Healthcare
  'dr reddy': { s: "BSE:DRREDDY", d: "Dr. Reddy's" },
  'cipla': { s: "BSE:CIPLA", d: "Cipla" },
  'divis': { s: "BSE:DIVISLAB", d: "Divi's Lab" },
  'apollo hospitals': { s: "BSE:APOLLOHOSP", d: "Apollo Hospitals" },
  'biocon': { s: "BSE:BIOCON", d: "Biocon" },
  'zydus': { s: "BSE:ZYDUSLIFE", d: "Zydus" },
  
  // IT & Tech Services
  'hcl': { s: "BSE:HCLTECH", d: "HCL Tech" },
  'tech mahindra': { s: "BSE:TECHM", d: "Tech Mahindra" },
  'l&t technology': { s: "BSE:LTTS", d: "L&T Tech" },
  'ltts': { s: "BSE:LTTS", d: "L&T Tech" },
  'mindtree': { s: "BSE:LTIM", d: "LTIMindtree" },
  'ltimindtree': { s: "BSE:LTIM", d: "LTIMindtree" },
  'mphasis': { s: "BSE:MPHASIS", d: "Mphasis" },
  
  // Semiconductors & Electronics Manufacturing
  'dixon': { s: "BSE:DIXON", d: "Dixon Tech" },
  'amber': { s: "BSE:AMBER", d: "Amber Ent" },
  'cg power': { s: "BSE:CGPOWER", d: "CG Power" },
  'kaynes': { s: "BSE:KAYNES", d: "Kaynes Tech" },
  'moschip': { s: "BSE:MOSCHIP", d: "Moschip" },
  'asm technologies': { s: "BSE:ASMTEC", d: "ASM Tech" },
  
  // Global Defense & Space & Semi (Publicly traded)
  'lockheed': { s: "NYSE:LMT", d: "Lockheed Martin" },
  'rtx': { s: "NYSE:RTX", d: "RTX" },
  'palantir': { s: "NYSE:PLTR", d: "Palantir" },
  'micron': { s: "NASDAQ:MU", d: "Micron" },
  'applied materials': { s: "NASDAQ:AMAT", d: "Applied Materials" },
  'lam research': { s: "NASDAQ:LRCX", d: "Lam Research" },
  'texas instruments': { s: "NASDAQ:TXN", d: "Texas Instruments" },
  'nxp': { s: "NASDAQ:NXPI", d: "NXP Semi" }
};

// Fallback mapping categories to relevant stock symbols
const categorySymbols: Record<string, { s: string, d: string }[]> = {
  tech: [
    { s: "NASDAQ:AAPL", d: "Apple" },
    { s: "NASDAQ:MSFT", d: "Microsoft" },
    { s: "NASDAQ:GOOGL", d: "Alphabet" }
  ],
  'ai-frontier': [
    { s: "NASDAQ:NVDA", d: "Nvidia" },
    { s: "NYSE:TSM", d: "TSMC" },
    { s: "NASDAQ:AMD", d: "AMD" }
  ],
  business: [
    { s: "AMEX:SPY", d: "S&P 500" },
    { s: "NASDAQ:TSLA", d: "Tesla" },
    { s: "NASDAQ:AMZN", d: "Amazon" }
  ],
  economy: [
    { s: "COMEX:GC1!", d: "Gold" },
    { s: "NYMEX:CL1!", d: "Crude Oil" },
    { s: "FX:EURUSD", d: "EUR/USD" }
  ],
  'health-science': [
    { s: "NYSE:PFE", d: "Pfizer" },
    { s: "NYSE:JNJ", d: "Johnson & Johnson" },
    { s: "NASDAQ:MRNA", d: "Moderna" }
  ]
};

function TradingViewWidgetComponent({ category = 'world', title = '', tags = [], articleText = '' }: TradingViewWidgetProps) {
  const container = useRef<HTMLDivElement>(null);

  // Dynamically extract companies from title, tags, and full article text
  const extractedSymbols: { s: string, d: string }[] = [];
  const safeTags = tags || [];
  const searchText = `${title} ${safeTags.join(' ')} ${articleText}`.toLowerCase();
  
  Object.keys(companyToSymbol).forEach(company => {
    // Use regex to ensure we match whole words (e.g., 'apple' but not 'pineapple')
    const regex = new RegExp(`\\b${company}\\b`, 'i');
    if (regex.test(searchText)) {
      extractedSymbols.push(companyToSymbol[company]);
    }
  });

  // If no specific companies are discussed in the article, DO NOT show the widget at all.
  // This prevents showing generic/default SPY/BTC in the article body.
  if (extractedSymbols.length === 0) {
    return null;
  }

  // Remove duplicates just in case, and allow up to 15 symbols
  const uniqueSymbolsMap = new Map();
  extractedSymbols.forEach(sym => uniqueSymbolsMap.set(sym.s, sym));
  const symbols = Array.from(uniqueSymbolsMap.values()).slice(0, 15);

  const symbolsJson = JSON.stringify(symbols);

  useEffect(() => {
    if (!container.current) return;
    container.current.innerHTML = '<div class="tradingview-widget-container__widget"></div>';

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js';
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.innerHTML = JSON.stringify({
      "colorTheme": "light",
      "dateRange": "12M",
      "showChart": true,
      "locale": "en",
      "largeChartUrl": "",
      "isTransparent": true,
      "showSymbolLogo": true,
      "showFloatingTooltip": true, // Enabled tooltips on hover/click
      "width": "100%",
      "height": "100%",
      "tabs": [
        {
          "title": "Market Movers",
          "symbols": JSON.parse(symbolsJson)
        }
      ]
    });
    
    container.current.appendChild(script);
  }, [category, symbolsJson]);

  return (
    <div className="my-12 border border-border/50 rounded-xl overflow-hidden bg-card/30 p-2 shadow-sm">
      <div className="flex items-center gap-2 mb-2 px-4 pt-2">
         <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
         <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Live Market Context</span>
      </div>
      
      <div className="relative w-full" style={{ height: '400px' }}>
        {/* The widget itself takes full 400px height so the scrollbar works perfectly */}
        <div className="tradingview-widget-container" ref={container} style={{ height: '100%', width: '100%' }}>
        </div>
      </div>

      {/* Official Compliant Affiliate Link */}
      <div className="border-t border-border/30 px-4 py-3 bg-muted/10 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Want to do deeper analysis on these stocks?
        </p>
        <a 
          href="https://www.tradingview.com/?aff_id=167158&source=worldprimepost"
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="text-xs font-semibold bg-brand/10 text-brand hover:bg-brand hover:text-white px-3 py-1.5 rounded-md transition-colors flex items-center gap-1"
        >
          Join TradingView Pro
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </a>
      </div>
    </div>
  );
}

export const TradingViewWidget = memo(TradingViewWidgetComponent);
